import { useEffect, useState } from 'react';
import { Shield, Search } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetAuditLog } from '../../services/api';

const EVENT_COLORS = {
  SERIAL_CREATED: 'text-blue-400',  SERIAL_ASSIGNED: 'text-blue-300',
  SERIAL_MARKED: 'text-purple-400', SERIAL_SHIPPED: 'text-teal-400',
  SHIPMENT_CREATED: 'text-orange-400', SHIPMENT_SHIPPED: 'text-teal-500',
  PACKET_GENERATED: 'text-yellow-400', PACKET_PRINTED: 'text-orange-300',
  PACKET_FILED: 'text-green-400',
  PAYMENT_PROCESSED: 'text-green-400', REFUND_ISSUED: 'text-red-400',
};

export default function AdminAuditLog() {
  const { token } = useAdminAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    adminGetAuditLog(token, typeFilter === 'all' ? null : typeFilter, 200)
      .then(setEvents).catch(console.error).finally(() => setLoading(false));
  }, [token, typeFilter]);

  const filtered = filter ? events.filter(e =>
    e.object_id?.toLowerCase().includes(filter.toLowerCase()) ||
    e.event_type?.toLowerCase().includes(filter.toLowerCase()) ||
    e.actor?.toLowerCase().includes(filter.toLowerCase())
  ) : events;

  return (
    <div data-testid="admin-audit-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider flex items-center gap-3">
            AUDIT LOG
            <span className="font-inter text-[10px] font-bold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5">APPEND-ONLY · IMMUTABLE</span>
          </h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">{events.length} events recorded · No deletions allowed</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search by object ID, event type, actor..."
            className="w-full bg-[#1e1e1e] border border-white/10 text-white font-inter text-sm pl-9 pr-4 py-2.5 outline-none focus:border-orange-500 transition-colors placeholder-zinc-600" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'serial_number', 'shipment', 'document_packet', 'order'].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`font-inter text-xs px-3 py-2 border transition-colors uppercase tracking-wider ${typeFilter === t ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500 hover:border-white/30'}`}>
              {t === 'all' ? 'All' : t.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="bg-[#1a1a1a] border border-white/10">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Shield size={14} className="text-orange-500" />
            <span className="font-inter text-xs text-zinc-500">
              {filtered.length} events · Append-only ledger · All events are permanent
            </span>
          </div>
          <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto" data-testid="audit-events-list">
            {filtered.map((ev, i) => (
              <div key={i} data-testid={`audit-event-${i}`} className="flex items-start gap-4 px-5 py-3 hover:bg-white/2 transition-colors">
                <div className="w-2 h-2 rounded-full bg-zinc-600 mt-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-inter text-xs font-semibold text-white font-mono">{ev.object_id}</span>
                    <span className={`font-inter text-[10px] font-bold uppercase tracking-wider ${EVENT_COLORS[ev.event_type] || 'text-zinc-400'}`}>{ev.event_type}</span>
                    <span className="font-inter text-[10px] text-zinc-600 bg-zinc-800 px-1.5 py-0.5">{ev.object_type}</span>
                  </div>
                  {ev.notes && <p className="font-inter text-xs text-zinc-400 mt-0.5">{ev.notes}</p>}
                  {ev.new_value && !ev.notes && <p className="font-inter text-xs text-zinc-600 mt-0.5 font-mono truncate">{JSON.stringify(ev.new_value)}</p>}
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="font-inter text-[10px] text-zinc-600">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}</span>
                    <span className="font-inter text-[10px] text-zinc-700">by {ev.actor || 'system'}</span>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div className="text-center py-12 font-inter text-sm text-zinc-600">No audit events found</div>}
          </div>
        </div>
      )}
    </div>
  );
}
